import React, { useState, useRef, useEffect, useCallback } from 'react';
import './StickyNote.css'; // Reuse existing styles
import RtfNoteFloat from './MainPanel/RtfNoteFloat';
import { useWorkspaces } from "./contexts/WorkspacesContext";
import { debounce } from 'lodash';

const NotePreviewSticky = ({ selectedNote, onClose,onRequestDelete,  isInMainPanel = true, forceMinimized = false,onExposePosition  }) => {
  
  if (!selectedNote) return null;

  // Use the Notes context functions
  const { 
    updateDocumentContent, 
    updateWorkspacePreviewPosition,
    saveStatus,
    getSelectedWorkspace
  } = useWorkspaces();
  const selectedWorkspace = getSelectedWorkspace();  
  const [isMinimized, setIsMinimized] = useState(forceMinimized);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const currentSizeRef = useRef({ width: 750, height: 950 });
  const [displayTitle, setDisplayTitle] = useState(selectedNote?.title || 'Untitled');
  const prevNoteIdRef = useRef(selectedNote?.public_id);
  const previousExpandedHeightRef = useRef(950);  
  
  // Initialize with workspace's preview values immediately
  const [position, setPosition] = useState({
    x: selectedWorkspace?.preview_x ?? 1,
    y: selectedWorkspace?.preview_y ?? 45
  });
  const [size, setSize] = useState({
    width: selectedWorkspace?.preview_width ?? 900,
    height: selectedWorkspace?.preview_height ?? 950
  });

  // ============================================================================
  // 🐛 DEBUG: Log initial state
  // ============================================================================
  useEffect(() => {
    console.log('🚀 NotePreviewSticky MOUNTED:', {
      workspace_id: selectedWorkspace?.public_id,
      initial_position: position,
      initial_size: size,
      initial_minimized: isMinimized,
      timestamp: new Date().toISOString()
    });
    
    return () => {
      console.log('💀 NotePreviewSticky UNMOUNTING:', {
        workspace_id: selectedWorkspace?.public_id,
        final_position: position,
        final_size: size,
        timestamp: new Date().toISOString()
      });
    };
  }, []);


  // ✅ ADD THIS - Expose current position to parent via ref
useEffect(() => {
  if (onExposePosition) {
    onExposePosition({
      preview_x: position.x,
      preview_y: position.y,
      preview_width: size.width,
      preview_height: size.height
    });
  }
}, [position, size, onExposePosition]);

  // Sync position/size with workspace when workspace changes
  useEffect(() => {
    console.log('🔄 useEffect triggered - workspace data changed:', {
      workspace_id: selectedWorkspace?.public_id,
      preview_x: selectedWorkspace?.preview_x,
      preview_y: selectedWorkspace?.preview_y,
      preview_width: selectedWorkspace?.preview_width,
      preview_height: selectedWorkspace?.preview_height,
      timestamp: new Date().toISOString(),
      calculated_shouldBeMinimized: (selectedWorkspace?.preview_height ?? 950) === 45
    });
    
    if (selectedWorkspace) {
      const newPosition = {
        x: selectedWorkspace.preview_x ?? 1,
        y: selectedWorkspace.preview_y ?? 45
      };
      
      const newSize = {
        width: selectedWorkspace.preview_width ?? 900,
        height: selectedWorkspace.preview_height ?? 950
      };
      
      console.log('📐 Applying new size:', newSize, 'at', new Date().toISOString()); 
      
      const shouldBeMinimized = newSize.height === 45;
      
      // Save expanded heights for later restoration
      if (newSize.height > 45) {
        previousExpandedHeightRef.current = newSize.height;
        console.log('💾 Saved expanded height:', newSize.height);
      }
      
      setPosition(newPosition);
      setSize(newSize);
      setIsMinimized(shouldBeMinimized);
      currentSizeRef.current = newSize;
    }
  }, [
    selectedWorkspace?.public_id,
    selectedWorkspace?.preview_x,
    selectedWorkspace?.preview_y,
    selectedWorkspace?.preview_width,
    selectedWorkspace?.preview_height
  ]);

  // ============================================================================
  // 🐛 DEBUG: Instrumented debounced save
  // ============================================================================
  const debouncedSavePosition = useCallback(
    debounce((newPos, newSize, workspacePublicId) => {
      const saveId = Date.now();
      console.log('⏰ DEBOUNCED SAVE EXECUTING:', {
        saveId,
        workspace_id: workspacePublicId,
        position: newPos,
        size: newSize,
        timestamp: new Date().toISOString()
      });
      
      if (workspacePublicId) {
        const payload = {
          preview_x: newPos.x,
          preview_y: newPos.y,
          preview_width: newSize.width,
          preview_height: newSize.height
        };
        
        console.log('📤 Calling updateWorkspacePreviewPosition with:', payload);
        
        try {
          const result = updateWorkspacePreviewPosition(workspacePublicId, payload);
          
          if (result && result.then) {
            result.then(() => {
              console.log('✅ Save completed successfully:', {
                saveId,
                workspace_id: workspacePublicId,
                timestamp: new Date().toISOString()
              });
            }).catch(err => {
              console.error('❌ Save failed:', {
                saveId,
                workspace_id: workspacePublicId,
                error: err,
                timestamp: new Date().toISOString()
              });
            });
          } else {
            console.log('✅ Save returned (non-promise):', result);
          }
        } catch (err) {
          console.error('❌ Save threw error:', err);
        }
      } else {
        console.warn('⚠️ No workspace ID - save skipped');
      }
    }, 1000),
    [updateWorkspacePreviewPosition]
  );

  // ============================================================================
  // 🐛 DEBUG: Track when debounce is triggered
  // ============================================================================
  const handlePositionChange = useCallback((newPos, newSize) => {
    console.log('🎯 handlePositionChange called:', {
      position: newPos,
      size: newSize,
      workspace_id: selectedWorkspace?.public_id,
      timestamp: new Date().toISOString()
    });
    
    setPosition(newPos);
    if (newSize) {
      setSize(newSize);
      currentSizeRef.current = newSize;
    }
    
    // Trigger debounced save with workspace ID
    if (selectedWorkspace?.public_id) {
      const sizeToSave = newSize || currentSizeRef.current;
      console.log('⏳ Triggering debounced save (1000ms)...', {
        workspace_id: selectedWorkspace.public_id,
        position: newPos,
        size: sizeToSave
      });
      debouncedSavePosition(newPos, sizeToSave, selectedWorkspace.public_id);
    } else {
      console.warn('⚠️ Cannot save - no workspace ID');
    }
  }, [selectedWorkspace?.public_id, debouncedSavePosition]);

  const toggleMinimize = () => {
    const newMinimized = !isMinimized;
    console.log('🔽 Toggle minimize:', {
      from: isMinimized,
      to: newMinimized,
      current_height: size.height,
      saved_expanded_height: previousExpandedHeightRef.current
    });
    
    setIsMinimized(newMinimized);
    
    let newHeight;
    if (newMinimized) {
      // Minimizing - save current height first, then set to 45
      if (size.height > 45) {
        previousExpandedHeightRef.current = size.height;
      }
      newHeight = 45;
    } else {
      // Un-minimizing - restore to previous expanded height
      newHeight = previousExpandedHeightRef.current;
    }
    
    const newSize = {
      width: size.width,
      height: newHeight
    };
    
    console.log('📏 New size after toggle:', newSize);
    
    setSize(newSize);
    currentSizeRef.current = newSize;
    handlePositionChange(position, newSize);
  };

  const handleContentChange = (content) => {
    if (selectedNote?.public_id) {
      updateDocumentContent(selectedNote.public_id, content);
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = currentSizeRef.current.width;
    const startHeight = currentSizeRef.current.height;

    console.log('🔧 Resize started:', { startWidth, startHeight });

    const handleMouseMove = (moveEvent) => {
      const newSize = {
        width: Math.max(300, startWidth + (moveEvent.clientX - startX)),
        height: Math.max(200, startHeight + (moveEvent.clientY - startY))
      };
      setSize(newSize);
      currentSizeRef.current = newSize;
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      console.log('🔧 Resize ended:', currentSizeRef.current);
      handlePositionChange(position, currentSizeRef.current);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    console.log('👆 Drag started');
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newPosition = {
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    };
    
    const constrainedPosition = constrainToScreen(newPosition, currentSizeRef.current);
    setPosition(constrainedPosition);
  };

  const handleMouseUp = () => {
    if (isDragging) {
      console.log('👆 Drag ended at position:', position);
      setIsDragging(false);
      handlePositionChange(position, currentSizeRef.current);
    }
  };

  const constrainToScreen = (pos, currentSize) => {
    const container = document.querySelector('.sticky-container') || 
                     document.querySelector('[class*="stickyContainer"]');
    
    const listPanel = document.querySelector('.stickies-list-panel-main');
    const listPanelHeight = listPanel ? listPanel.offsetHeight : 30;
    
    const BOTTOM_COMPENSATION = 5;
    
    const maxX = container ? container.clientWidth - currentSize.width : 
                 window.innerWidth - 260 - currentSize.width;
    
    const maxY = container ? container.clientHeight - listPanelHeight - currentSize.height + BOTTOM_COMPENSATION : 
                 window.innerHeight - currentSize.height;
    
    return {
      x: Math.max(0, Math.min(pos.x, maxX)),
      y: Math.max(0, Math.min(pos.y, maxY))
    };
  };

  // Debounced title update
  const debouncedUpdateTitle = useCallback(
    debounce((newTitle) => {
      setDisplayTitle(newTitle || 'Untitled');
    }, 1000),
    []
  );

  useEffect(() => {
    if (selectedNote) {
      if (prevNoteIdRef.current !== selectedNote.public_id) {
        setDisplayTitle(selectedNote.title || 'Untitled');
        prevNoteIdRef.current = selectedNote.public_id;
        debouncedUpdateTitle.cancel();
      } else {
        debouncedUpdateTitle(selectedNote.title);
      }
    }
  }, [selectedNote?.public_id, selectedNote?.title, debouncedUpdateTitle]);

  useEffect(() => {
    return () => {
      debouncedUpdateTitle.cancel();
    };
  }, [debouncedUpdateTitle]);

  // Handle drag events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, position]);

  // ============================================================================
  // 🐛 DEBUG: Log cleanup
  // ============================================================================
  useEffect(() => {
    return () => {
      console.log('🧹 Cleanup running for debounced save');
      console.log('🧹 Pending debounced calls will be:', 
        debouncedSavePosition.cancel ? 'CANCELLED' : 'ALLOWED TO COMPLETE');
      debouncedSavePosition.cancel();
    };
  }, [debouncedSavePosition]);

  return (
    <div
      className={`sticky note-preview-sticky ${isMinimized ? 'minimized' : ''}`}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: isMinimized ? 45 : size.height,
        zIndex: 1000 
      }}
    >
      <div 
        className="drag-bar" 
        onMouseDown={handleMouseDown}
        onDoubleClick={toggleMinimize}
      >
        <span>📄 Document - {displayTitle}</span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {saveStatus.status !== 'idle' && (
            <div className={`save-indicator-pill ${saveStatus.status}`}>
              {saveStatus.status === 'saving' && '⏳ Saving...'}
              {saveStatus.status === 'saved' && '✓ Saved'}
              {saveStatus.status === 'error' && '⚠ Error'}
            </div>
          )}
          
          <div className="sticky-controls">
            <button onClick={toggleMinimize}>
              {isMinimized ? '□' : '−'}
            </button>
            <button onClick={onRequestDelete}>✖</button> 
          </div>
        </div>
      </div>
      
      {!isMinimized && (
        <div 
          style={{
            height: 'calc(100% - 45px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <RtfNoteFloat
            initialValue={selectedNote.content || ''}
            onChange={handleContentChange}
            noteId={selectedNote.public_id}
          />
        </div>
      )}

      {!isMinimized && (
        <div className="resize-handle" onMouseDown={handleResizeStart} />
      )}
    </div>
  );
};

export default NotePreviewSticky;